import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { getUserFriendlyErrorMessage } from "api/UserFriendlyError";
import { getImageDimensions } from "libs/files";
import { convertCustomerUploadsS3ToCloudFront } from "libs/media-files";
import { PostMediaFile } from "shared/types/post";

export interface PostMediaEditingState {
  isModalOpened: boolean;
  selectedFile: PostMediaFile | null;
  progress: number | null;
}

type PostMediaEditingAction =
  | {
      type: "fileSelected";
      payload: PostMediaFile;
    }
  | {
      type: "editorClosed";
    }
  | {
      type: "progressChanged";
      payload: number | null;
    }
  | {
      type: "uploadSucceeded";
    }
  | {
      type: "uploadFailed";
    };

export const initialPostMediaEditingState: PostMediaEditingState = {
  isModalOpened: false,
  selectedFile: null,
  progress: null,
};

export function postMediaEditingReducer(
  state: PostMediaEditingState,
  action: PostMediaEditingAction
): PostMediaEditingState {
  switch (action.type) {
    case "fileSelected": {
      return {
        isModalOpened: true,
        selectedFile: action.payload,
        progress: null,
      };
    }
    case "editorClosed":
    case "uploadSucceeded": {
      return initialPostMediaEditingState;
    }
    case "progressChanged": {
      return {
        ...state,
        progress: action.payload,
      };
    }
    case "uploadFailed": {
      return state;
    }
    default: {
      return state;
    }
  }
}

export async function uploadFileToS3(
  blob: Blob
): Promise<{ fileUrl: string; thumbnailUrl: string }> {
  const {
    data: { fileUrl, thumbnailUrl, uploadUrl },
  } = await axios.post("/getSignedUrl", {
    contentType: "image/png",
    fileExtension: ".png",
  });

  await axios.put(uploadUrl, blob);

  return { fileUrl, thumbnailUrl };
}

export async function uploadEditedFile({
  previousFile,
  newBlob,
}: {
  previousFile: PostMediaFile;
  newBlob: Blob;
}): Promise<PostMediaFile> {
  const { thumbnailUrl, fileUrl } = await uploadFileToS3(newBlob);

  return {
    originalUrl: previousFile.originalUrl,
    altText: previousFile.altText,
    fileName: previousFile.fileName,
    isVideo: false,
    thumbnailUrl,
    url: fileUrl,
    type: "image",
    asset_id: previousFile.asset_id,
    meta: {
      ...(await getImageDimensions(newBlob)),
      size: newBlob.size / 1024,
      byteSize: newBlob.size,
    },
  };
}

interface PostMediaEditingContextValue {
  state: PostMediaEditingState;
  fileSrc: string;
  onEdit(file: PostMediaFile): void;
  onClose(): void;
  onSave(blob: Blob): Promise<void>;
}

const PostMediaEditingContext =
  createContext<PostMediaEditingContextValue | null>(null);

export function PostMediaEditingProvider({
  children,
  onChange,
}: {
  children: React.ReactNode;
  onChange(file: PostMediaFile): void;
}) {
  const [state, dispatch] = useReducer(
    postMediaEditingReducer,
    initialPostMediaEditingState
  );

  const onEdit = useCallback((file: PostMediaFile) => {
    dispatch({
      type: "fileSelected",
      payload: file,
    });
  }, []);

  const onClose = useCallback(() => {
    dispatch({
      type: "editorClosed",
    });
  }, []);

  const onSave = useCallback(
    async (blob: Blob) => {
      if (!state.selectedFile) {
        return;
      }

      dispatch({
        type: "progressChanged",
        payload: 100,
      });

      try {
        const nextFile = await uploadEditedFile({
          previousFile: state.selectedFile,
          newBlob: blob,
        });

        onChange(nextFile);
        dispatch({
          type: "uploadSucceeded",
        });
      } catch (error) {
        toast.error(getUserFriendlyErrorMessage(error as Error));
        dispatch({
          type: "uploadFailed",
        });
      }
    },
    [onChange, state.selectedFile]
  );

  const value = useMemo(
    () => ({
      state,
      fileSrc: convertCustomerUploadsS3ToCloudFront(
        state.selectedFile?.originalUrl || state.selectedFile?.url || ""
      ),
      onEdit,
      onClose,
      onSave,
    }),
    [state, onClose, onEdit, onSave]
  );

  return (
    <PostMediaEditingContext.Provider value={value}>
      {children}
    </PostMediaEditingContext.Provider>
  );
}

export function usePostMediaEditingModel() {
  const context = useContext(PostMediaEditingContext);

  if (!context) {
    throw new Error(
      "usePostMediaEditingModel must be used within PostMediaEditingProvider"
    );
  }

  return context;
}
